import { useMemo, useState, useEffect } from 'react';
import {
  Bed,
  Bath,
  Car,
  Maximize,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Play,
  ExternalLink,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MessageCircle } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import {
  formatCurrency,
  formatArea,
  propertyTypeLabels,
  transactionTypeLabels,
  getPropertyImage,
  getWhatsAppLink,
} from '../lib/utils';

export const PropertyCard = ({ property, onClick }) => {
  const mainImage = getPropertyImage(property, 0);
  const price =
    property.transaction_type === 'rent'
      ? property.pricing?.rent_price
      : property.pricing?.sale_price;
  const priceLabel = property.transaction_type === 'rent' ? '/mês' : '';

  return (
    <div
      data-testid={`property-card-${property.id}`}
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
      onClick={() => onClick?.(property)}
    >
      <div className="relative h-60 overflow-hidden bg-slate-100">
        <img
          src={mainImage}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />

        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {property.featured && (
            <Badge className="bg-brand-gold text-brand-blue font-semibold">
              Destaque
            </Badge>
          )}

          {property.tags?.slice(0, 2).map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="bg-white/95 text-slate-700"
            >
              {tag}
            </Badge>
          ))}
        </div>

        <div className="absolute bottom-3 right-3">
          <Badge className="bg-brand-blue text-white">
            {transactionTypeLabels[property.transaction_type]}
          </Badge>
        </div>
      </div>

      <div className="p-5">
        <div className="mb-2">
          {price ? (
            <>
              <span className="text-2xl font-bold text-brand-blue">
                {formatCurrency(price)}
              </span>
              <span className="text-slate-500 text-sm ml-1">{priceLabel}</span>
            </>
          ) : (
            <span className="bg-yellow-400 text-yellow-900 font-bold text-sm px-3 py-1 rounded">
              CONSULTE
            </span>
          )}
        </div>

        <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2 line-clamp-2 min-h-[56px]">
          {property.title}
        </h3>

        <div className="flex items-center text-slate-500 text-sm mb-4">
          <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
          <span>
            {property.address?.neighborhood || 'Toledo'},{' '}
            {property.address?.city || 'PR'}
          </span>
        </div>

        <div className="flex items-center gap-4 text-slate-600 text-sm flex-wrap">
          {property.rooms?.bedrooms > 0 && (
            <div className="flex items-center gap-1">
              <Bed className="w-4 h-4" />
              <span>{property.rooms.bedrooms}</span>
            </div>
          )}

          {property.rooms?.bathrooms > 0 && (
            <div className="flex items-center gap-1">
              <Bath className="w-4 h-4" />
              <span>{property.rooms.bathrooms}</span>
            </div>
          )}

          {property.garage?.parking_spots > 0 && (
            <div className="flex items-center gap-1">
              <Car className="w-4 h-4" />
              <span>{property.garage.parking_spots}</span>
            </div>
          )}

          {property.sizes?.built_area_m2 && (
            <div className="flex items-center gap-1">
              <Maximize className="w-4 h-4" />
              <span>{property.sizes.built_area_m2}m²</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <Badge variant="outline" className="text-xs">
            {propertyTypeLabels[property.property_type]}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export const PropertyModal = ({ property, open, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [property?.id, open]);

  const images = useMemo(() => {
    if (!property) return [];

    const rawImages =
      property.images?.length > 0 ? property.images : [getPropertyImage(property)];

    return rawImages.map((img) =>
      typeof img === 'string' && !img.startsWith('http')
        ? `${process.env.REACT_APP_BACKEND_URL}${img}`
        : img
    );
  }, [property]);

  if (!property) return null;

  const price =
    property.transaction_type === 'rent'
      ? property.pricing?.rent_price
      : property.pricing?.sale_price;

  const priceLabel = property.transaction_type === 'rent' ? '/mês' : '';

  const propertyUrl = `${window.location.origin}/?imovel=${property.id}`;
  
  // botão principal - lead
  const contactMessage = `Olá! Tenho interesse neste imóvel:\n${property.title}\n${propertyUrl}`;
  const whatsappContactLink = getWhatsAppLink('5545999092653', contactMessage);
  
  // botão pequeno - compartilhamento
  const shareMessage = `Dá uma olhada nesse imóvel:\n${propertyUrl}`;
  const whatsappShareLink = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;

  const goPrevImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const goNextImage = () => {
    if (!images.length) return;
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const locationText = property.address?.hide_exact_address
    ? `${property.address?.neighborhood || ''}, ${property.address?.city || 'Toledo'}`
    : `${property.address?.street || ''} ${property.address?.number || ''}, ${property.address?.neighborhood || ''}, ${property.address?.city || 'Toledo'}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 rounded-2xl"
        data-testid="property-modal"
      >
        <div className="bg-white">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* GALERIA */}
            <div className="bg-slate-100">
              <div className="relative h-[280px] sm:h-[380px] lg:h-[520px] overflow-hidden">
                <img
                  src={images[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />

                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 hover:bg-black/60 text-white flex items-center justify-center transition"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <button
                      type="button"
                      onClick={goNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/45 hover:bg-black/60 text-white flex items-center justify-center transition"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <Badge className="bg-brand-blue text-white">
                    {transactionTypeLabels[property.transaction_type]}
                  </Badge>
                  <Badge className="bg-white/90 text-slate-800 border border-slate-200">
                    {propertyTypeLabels[property.property_type]}
                  </Badge>
                  {property.featured && (
                    <Badge className="bg-brand-gold text-brand-blue font-semibold">
                      Destaque
                    </Badge>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="absolute bottom-4 right-4 bg-black/55 text-white text-sm px-3 py-1 rounded-full">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>

              {images.length > 1 && (
                <div className="p-4 bg-white border-t border-slate-200">
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {images.map((img, index) => (
                      <button
                        key={`${img}-${index}`}
                        type="button"
                        onClick={() => setCurrentImageIndex(index)}
                        className={`relative flex-shrink-0 rounded-xl overflow-hidden border-2 transition ${
                          currentImageIndex === index
                            ? 'border-brand-blue'
                            : 'border-transparent'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Imagem ${index + 1}`}
                          className="w-24 h-20 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* SHARE WHATSAPP */}
              <div className="px-4 py-3 bg-white border-t border-slate-200 flex items-center gap-2 text-sm">
                <span className="text-slate-500">Compartilhar:</span>

                <a
                  href={whatsappShareLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-green-600 hover:text-white hover:bg-green-600 px-2 py-1 rounded-md transition"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>

            {/* CONTEÚDO */}
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 leading-tight mb-3">
                  {property.title}
                </h2>

                <div className="text-3xl md:text-4xl font-bold text-brand-blue mb-3">
                  {price ? (
                    <>
                      {formatCurrency(price)}
                      <span className="text-slate-500 text-base md:text-lg font-normal ml-2">
                        {priceLabel}
                      </span>
                    </>
                  ) : (
                    <span className="bg-yellow-400 text-yellow-900 font-bold text-lg px-4 py-1.5 rounded">
                      CONSULTE
                    </span>
                  )}
                </div>

                <div className="flex items-center text-slate-500">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{locationText}</span>
                </div>

                {property.pricing?.condo_fee && (
                  <div className="text-sm text-slate-500 mt-2">
                    Condomínio: {formatCurrency(property.pricing.condo_fee)}/mês
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                {property.rooms?.bedrooms > 0 && (
                  <div className="flex items-center gap-3">
                    <Bed className="w-5 h-5 text-brand-blue" />
                    <div>
                      <div className="font-semibold text-slate-800">
                        {property.rooms.bedrooms}
                      </div>
                      <div className="text-xs text-slate-500">Quartos</div>
                    </div>
                  </div>
                )}

                {property.rooms?.suites > 0 && (
                  <div className="flex items-center gap-3">
                    <Bed className="w-5 h-5 text-brand-blue" />
                    <div>
                    <div className="font-semibold text-slate-800">
                      {property.rooms.suites}
                    </div>
                    <div className="text-xs text-slate-500">Suítes</div>
                  </div>
                </div>
              )}

                {property.rooms?.bathrooms > 0 && (
                  <div className="flex items-center gap-3">
                    <Bath className="w-5 h-5 text-brand-blue" />
                    <div>
                      <div className="font-semibold text-slate-800">
                        {property.rooms.bathrooms}
                      </div>
                      <div className="text-xs text-slate-500">Banheiros</div>
                    </div>
                  </div>
                )}

                {property.rooms?.balconies > 0 && (
                  <div className="flex items-center gap-3">
                    <Maximize className="w-5 h-5 text-brand-blue" />
                    <div>
                      <div className="font-semibold text-slate-800">
                        {property.rooms.balconies}
                      </div>
                      <div className="text-xs text-slate-500">Varandas</div>
                    </div>
                  </div>
                )}


                {property.garage?.parking_spots > 0 && (
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-brand-blue" />
                    <div>
                      <div className="font-semibold text-slate-800">
                        {property.garage.parking_spots}
                      </div>
                      <div className="text-xs text-slate-500">Vagas</div>
                    </div>
                  </div>
                )}

                {property.sizes?.built_area_m2 && (
                  <div className="flex items-center gap-3">
                    <Maximize className="w-5 h-5 text-brand-blue" />
                    <div>
                      <div className="font-semibold text-slate-800">
                        {formatArea(property.sizes.built_area_m2)}
                      </div>
                      <div className="text-xs text-slate-500">Área Construída</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-semibold text-lg mb-3 text-slate-800">
                    Descrição
                  </h3>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {property.description || 'Sem descrição disponível.'}
                  </p>
                </div>

                {property.amenities?.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-3 text-slate-800">
                      Comodidades
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {property.amenities.map((amenity) => (
                        <Badge
                          key={amenity}
                          variant="outline"
                          className="py-1.5 px-3 rounded-full"
                        >
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {(property.video_url || property.virtual_tour_url) && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="font-heading font-semibold text-lg mb-4 text-slate-800">
                      Vídeo e tour virtual
                    </h3>

                    <div className="flex flex-col sm:flex-row gap-3">
                      {property.video_url && (
                        <Button
                          asChild
                          className="bg-brand-blue hover:bg-brand-blue-dark text-white rounded-full"
                        >
                          <a
                            href={property.video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Ver vídeo
                          </a>
                        </Button>
                      )}

                      {property.virtual_tour_url && (
                        <Button
                          asChild
                          variant="outline"
                          className="rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
                        >
                          <a
                            href={property.virtual_tour_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Abrir tour virtual
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-6 border-t border-slate-200">
                <Button
                  data-testid="whatsapp-modal-btn"
                  asChild
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white rounded-full"
                >
                  <a href={whatsappContactLink} target="_blank" rel="noopener noreferrer">
                    Falar no WhatsApp
                  </a>
                </Button>

                <Button
                  data-testid="call-modal-btn"
                  asChild
                  variant="outline"
                  className="flex-1 h-12 rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
                >
                  <a href="tel:+5545999092653">Ligar Agora</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};